import React, { useEffect, useState } from 'react';
import { SortOrder, TableColumn } from 'react-data-table-component';

import { DefaultTable } from 'src/components';

import { IBenefitsReview } from 'src/interfaces';

import { useQuery } from '@tanstack/react-query';
import BenefitsService from 'src/services/benefits.service';

import { monetaryUSDFormat } from 'src/utils/currencyFormat';
import { Container } from './styles';
import { ColumnHeader } from '../current-benefits/styles';

const columns: TableColumn<IBenefitsReview>[] = [
  {
    name: <ColumnHeader>Benefit ID</ColumnHeader>,
    selector: (row: IBenefitsReview) => row.benefitId,
    sortField: 'benefitId',
    sortable: true,
    wrap: true,
  },
  {
    name: <ColumnHeader>Elected Date</ColumnHeader>,
    selector: (row: IBenefitsReview) => row.electedDate || '',
    sortField: 'createdAt',
    sortable: true,
    wrap: true,
  },
  {
    name: <ColumnHeader>Plan ID</ColumnHeader>,
    selector: (row: IBenefitsReview) => row.plan,
    sortField: 'planId',
    sortable: true,
    wrap: true,
  },
  {
    name: <ColumnHeader>Dependents</ColumnHeader>,
    selector: (row: IBenefitsReview) => row.dependents,
    sortField: 'dependents',
    sortable: true,
    wrap: true,
  },
  {
    name: <ColumnHeader>Type</ColumnHeader>,
    selector: (row: IBenefitsReview) => row.type,
    sortField: 'type',
    sortable: true,
    wrap: true,
  },

  {
    name: <ColumnHeader>Status</ColumnHeader>,
    selector: (row: IBenefitsReview) => row.status,
    sortField: 'status',
    sortable: true,
    wrap: true,
  },
  {
    name: <ColumnHeader>Approval Status</ColumnHeader>,
    selector: (row: IBenefitsReview) => row?.approvalStatus || '',

    wrap: true,
  },
  {
    name: <ColumnHeader>Semi-Monthly</ColumnHeader>,
    selector: (row: IBenefitsReview) => monetaryUSDFormat(row.price),
    sortField: 'cost',
    sortable: true,
    wrap: true,
  },
  {
    name: <ColumnHeader>Monthly Price</ColumnHeader>,
    selector: (row: IBenefitsReview) => monetaryUSDFormat(row.price * 2),
    sortField: 'cost',
    sortable: true,
    wrap: true,
  },
  {
    name: <ColumnHeader>Annual Cost</ColumnHeader>,
    selector: (row: IBenefitsReview) => monetaryUSDFormat(row.annualCost),
    sortField: 'annual',
    sortable: true,
    wrap: true,
  },
  {
    name: <ColumnHeader>Coverage Amount</ColumnHeader>,
    selector: (row: IBenefitsReview) => monetaryUSDFormat(row.coverageAmount),
    sortField: 'coverageAmount',
    sortable: true,
    wrap: true,
  },
];

export const BenefitsSummaryHistory: React.FC = () => {
  const [data, setData] = useState<IBenefitsReview[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sortBy, setSortBy] = useState('');

  const [sortDirection, setSortDirection] = useState('');

  const {
    data: benefitsItems,
    refetch,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ['benefits-summary-history'],
    queryFn: () =>
      BenefitsService.getBenefitsSummaryHistory({
        page,
        limit,
        sortBy,
        sortDirection,
      }),
    enabled: true,
  });

  useEffect(() => {
    refetch();
  }, [page, limit, sortBy, sortDirection]);

  useEffect(() => {
    if (benefitsItems) {
      setData(benefitsItems.items);
      setTotalRows(benefitsItems.meta?.totalItems as unknown as 0);
    }
  }, [benefitsItems]);

  const handlePageChange = (nPage: number) => {
    setPage(nPage);
  };

  const handlePerRowsChange = async (newLimit: number, nPage: number) => {
    setPage(nPage);
    setLimit(newLimit);
  };

  const handleSort = async (column: TableColumn<IBenefitsReview>, newSortDirection: SortOrder) => {
    setSortBy(column.sortField as unknown as string);
    setSortDirection(newSortDirection);
  };

  return (
    <Container>
      <DefaultTable
        columns={columns}
        data={data}
        progressPending={isLoading || isFetching}
        pagination
        paginationServer
        paginationTotalRows={totalRows}
        onChangeRowsPerPage={handlePerRowsChange}
        onChangePage={handlePageChange}
        onSort={handleSort}
        sortServer
      />
    </Container>
  );
};
